import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const backendLayers = [
  {
    title: '24个观测点',
    desc: '记录课堂事实：提问频次、等待时长、学生回应数量、追问情况',
    icon: 'ri-eye-line',
    color: 'primary',
  },
  {
    title: '17个打分点',
    desc: '将事实组织成维度判断：互动不足、案例支撑、理论深度、价值引领',
    icon: 'ri-scales-3-line',
    color: 'accent',
  },
  {
    title: '5类底线提示',
    desc: '集中标记需复核的事实、合规和争议线索，专用模型标出19门待复核',
    icon: 'ri-alert-line',
    color: 'primary',
  },
];

const exampleDimensions = [
  { label: '互动', desc: '提问频次→等待时长→回应数量→追问→回应进入教学' },
  { label: '案例', desc: '出现位置→是否提出问题→是否进入理论→是否完成价值回扣' },
  { label: '理论', desc: '概念出现→概念关系→命题展开→专题完成度' },
];

export default function P35BackendStructure({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <span className="inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700">后台结构</span>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            后台观测点、打分点与<span className="text-primary-600">底线提示</span>
          </h2>
        </div>

        {/* 三层结构 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-200">
          {backendLayers.map((layer, i) => (
            <div key={layer.title} className={`p-5 rounded-2xl transition-all duration-500 hover:scale-[1.02] ${layer.color === 'primary' ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`} style={{ transitionDelay: `${200 + i * 120}ms` }}>
              <div className="relative z-10 text-center">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${layer.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/40'}`}>
                  <i className={`${layer.icon} text-lg ${layer.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
                </div>
                <h3 className={`font-heading text-lg font-bold mb-2 ${layer.color === 'primary' ? 'text-primary-700' : 'text-accent-700'}`}>{layer.title}</h3>
                <p className="text-xs text-foreground-500 font-body leading-relaxed">{layer.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 维度拆解示例 */}
        <div className="max-w-4xl mx-auto mb-5 transition-all duration-1000 delay-500">
          <h3 className="font-heading text-xs font-bold text-foreground-800 mb-3 flex items-center gap-2">
            <i className="ri-arrow-right-s-line text-primary-500" />
            观测点→评价的层级设计
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {exampleDimensions.map((dim) => (
              <div key={dim.label} className="p-3 rounded-2xl glass border-white/50">
                <p className="text-xs font-body font-bold text-foreground-800 mb-1.5">{dim.label}</p>
                <div className="flex flex-wrap items-center gap-1">
                  {dim.desc.split('→').map((step, j) => (
                    <span key={j} className="flex items-center gap-1">
                      <span className="text-[10px] text-foreground-500 font-body">{step.trim()}</span>
                      {j < dim.desc.split('→').length - 1 && (
                        <i className="ri-arrow-right-s-line text-foreground-300 text-[10px]" />
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 底线提示数据 */}
        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="px-4 py-2 rounded-xl glass-gold border-accent-200/30 text-xs text-foreground-500 font-body">
              专用模型标出<span className="font-bold text-accent-700">19门</span>待复核底线线索
            </div>
            <div className="px-4 py-2 rounded-xl glass border-white/50 text-xs text-foreground-500 font-body">
              通用模型仅标出<span className="font-bold text-foreground-700">1门</span>
            </div>
            <div className="px-4 py-2 rounded-xl glass border-white/50 text-xs text-foreground-400 font-body">
              标得更多≠误报更少，所有线索需按规则复核
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}